// DS Bundle: the product-page block and the homepage sets add several products at once.
// Option A (2026-10-15): no bundle discount. Each bundle opens with just enough items to reach the
// next Cart Ladder reward, and the summary says which reward the selection unlocks.
// Each store lists its own tiers in snippets/ds-ladder.liquid; they must match that store's
// Shopify automatic discounts and snippets/cart-discount-ladder.liquid.
const NOT_SHIRTS = ['shipping insurance', 'shipping protection'];

const TEXT = {
  unlock: 'Unlocks {reward}',
  toSpend: 'Spend {money} more for {reward}',
  toShirt: 'Add {count} more {shirts} to unlock {reward}',
  addOne: 'Add 1 item to cart',
  addMany: 'Add {count} items to cart',
  pick: 'Select items',
  adding: 'Adding…',
  error: 'Could not add to cart. Please try again.'
};

const root = window.Shopify?.routes?.root || '/';
const bundles = new Set();
// Homepage sets: products already shown in a set during this visit, so the other sets skip them.
const seen = new Set();
let cartRequest = null;

function formatMoney(cents) {
  const format = window.themeHDN?.settings?.moneyFormat || '${{amount}}';
  const value = cents / 100;
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
    if (key === 'amount_no_decimals') return Math.round(value).toLocaleString('en-US');
    if (key === 'amount_with_comma_separator') return value.toFixed(2).replace('.', ',');
    if (key === 'amount_no_decimals_with_comma_separator') return Math.round(value).toLocaleString('de-DE');
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  });
}

function loadCart(fresh = false) {
  if (!cartRequest || fresh) {
    cartRequest = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }
  return cartRequest;
}

// Same counting as snippets/cart-discount-ladder.liquid.
function summarize(cart) {
  const state = { total: 0, original: 0, shirts: 0, products: new Set() };
  if (!cart) return state;
  state.total = cart.total_price || 0;
  for (const item of cart.items || []) {
    state.products.add(item.product_id);
    // Canvas prints are not shirts: they count toward free shipping (state.total) but not the $10 or the free shirt.
    if (String(item.product_type || '').toLowerCase() === 'canvas') continue;
    if (NOT_SHIRTS.includes(String(item.product_title || '').toLowerCase())) continue;
    state.shirts += item.quantity;
    state.original += item.original_line_price ?? item.line_price ?? 0;
  }
  return state;
}

// Tiers, lowest first: { kind: 'total' (after discounts) | 'original' (before) | 'shirts', min, reward, more, last? }.
let tiersCache = null;
function tiers() {
  if (!tiersCache) {
    try {
      tiersCache = JSON.parse(document.getElementById('ds-ladder')?.textContent || '{}').tiers || [];
    } catch (error) {
      tiersCache = [];
    }
  }
  return tiersCache;
}

function tierReached(tier, total, original, shirts) {
  if (tier.kind === 'shirts') return shirts >= tier.min;
  return (tier.kind === 'total' ? total : original) >= tier.min;
}

// 0 = nothing yet, otherwise the number of the highest tier reached.
function rewardLevel(total, original, shirts) {
  let level = 0;
  tiers().forEach((tier, index) => {
    if (tierReached(tier, total, original, shirts)) level = index + 1;
  });
  return level;
}

class BundleItem {
  constructor(el) {
    this.el = el;
    this.main = el.classList.contains('is-main');
    this.fallback = el.hasAttribute('data-ds-fallback');
    this.productId = Number(el.dataset.productId);
    const data = JSON.parse(el.querySelector('[data-ds-variants]').textContent);
    this.images = data.imgs;
    this.variants = data.v.map(([id, options, price, compare, image]) => ({ id, options, price, compare, image }));
    this.check = el.querySelector('[data-ds-check]');
    this.controls = [...el.querySelectorAll('[data-ds-opt]')];
    this.current = this.variants.find((variant) => variant.id === Number(el.dataset.variantId)) || this.variants[0];
    this.picked = this.current ? [...this.current.options] : [];
  }

  get checked() {
    return this.main || this.check.checked;
  }

  set checked(value) {
    if (!this.main) this.check.checked = value;
  }

  get price() {
    return this.current ? this.current.price : 0;
  }

  // A value is offered when some variant has it together with the options picked before it
  // (shirt type → color → size).
  offers(index, value) {
    return this.variants.some(
      (variant) => variant.options[index] === value && variant.options.slice(0, index).every((option, i) => option === this.picked[i])
    );
  }

  choose(index, value) {
    this.picked[index] = value;
    let match = this.variants.find((variant) => variant.options.every((option, i) => option === this.picked[i]));
    if (!match) {
      // Keep the new value and as many of the earlier picks as possible.
      let bestScore = -1;
      for (const variant of this.variants) {
        if (variant.options[index] !== value) continue;
        const score = variant.options.reduce((sum, option, i) => sum + (option === this.picked[i] ? 2 ** (8 - i) : 0), 0);
        if (score > bestScore) {
          bestScore = score;
          match = variant;
        }
      }
    }
    if (match) this.current = match;
    this.picked = [...this.current.options];
    this.render();
  }

  selectVariant(id) {
    const variant = this.variants.find((item) => item.id === id);
    if (!variant) return false;
    this.current = variant;
    this.picked = [...variant.options];
    this.render();
    return true;
  }

  render() {
    const variant = this.current;
    if (!variant) return;
    const image = this.el.querySelector('[data-ds-img]');
    const src = this.images[variant.image] || this.images['0'];
    if (image && src && image.getAttribute('src') !== src) image.src = src;

    const price = this.el.querySelector('[data-ds-price]');
    const compare = this.el.querySelector('[data-ds-compare]');
    const onSale = variant.compare > variant.price;
    price.textContent = formatMoney(variant.price);
    price.classList.toggle('is-sale', onSale);
    compare.hidden = !onSale;
    if (onSale) compare.textContent = formatMoney(variant.compare);

    for (const control of this.controls) {
      const index = Number(control.dataset.dsOpt);
      if (control.tagName === 'SELECT') {
        for (const option of control.options) option.disabled = !this.offers(index, option.value);
        control.value = this.picked[index];
      } else {
        const active = control.dataset.value === this.picked[index];
        control.hidden = !this.offers(index, control.dataset.value);
        control.classList.toggle('is-active', active);
        control.setAttribute('aria-pressed', String(active));
        if (active) {
          const label = this.el.querySelector('[data-ds-color-label]');
          if (label) label.textContent = control.dataset.label;
        }
      }
    }
    this.el.classList.toggle('is-off', !this.checked);
  }
}

class DsBundle extends HTMLElement {
  connectedCallback() {
    if (this.items) return;
    this.items = [...this.querySelectorAll('[data-ds-item]')].map((el) => new BundleItem(el));
    this.min = Number(this.dataset.min) || 2;
    this.totalEl = this.querySelector('[data-ds-total]');
    this.compareEl = this.querySelector('[data-ds-total-compare]');
    this.rewardEl = this.querySelector('[data-ds-reward]');
    this.addButton = this.querySelector('[data-ds-add]');
    this.addLabel = this.querySelector('[data-ds-add-label]');
    this.statusEl = this.querySelector('[data-ds-status]');

    this.addEventListener('change', (event) => {
      const item = this.itemFor(event.target);
      if (!item) return;
      if (event.target.matches('select[data-ds-opt]')) item.choose(Number(event.target.dataset.dsOpt), event.target.value);
      else item.render();
      this.update();
    });
    this.addEventListener('click', (event) => {
      const swatch = event.target.closest('button[data-ds-opt]');
      if (swatch) {
        this.itemFor(swatch)?.choose(Number(swatch.dataset.dsOpt), swatch.dataset.value);
        this.update();
      } else if (event.target.closest('[data-ds-add]')) {
        this.add();
      } else if (event.target.closest('[data-ds-switch]')) {
        this.showNext();
      }
    });

    // Keep "This item" on the variant chosen in the main picker.
    const main = this.items.find((item) => item.main);
    if (main) {
      (this.closest('.shopify-section') || document).addEventListener('variant:change', (event) => {
        const id = event.detail?.variant?.id;
        if (id && main.selectVariant(id)) this.update();
      });
    }

    // Homepage sets page through their collection (sections/ds-bundle-feed.liquid), best sellers first.
    this.max = Number(this.dataset.max) || 4;
    this.feed = this.dataset.feed || '';
    this.listEl = this.querySelector('.ds-bundle__list');
    this.queue = [];
    this.known = new Set(this.items.map((item) => item.productId));
    this.mine = new Set();
    this.page = 0;
    this.pages = Infinity;
    this.fresh = false;
    this.task = Promise.resolve();

    const switcher = this.querySelector('[data-ds-switch]');
    if (switcher) switcher.hidden = this.sets().length < 2;

    this.items.forEach((item) => item.render());
    this.update();
    bundles.add(this);
    if (this.feed && !this.items.length) {
      // Not rendered by Liquid: load it now if it is the set on screen.
      if (!this.hidden) this.refill().then(() => this.empty && this.showNext());
    } else {
      loadCart().then((cart) => {
        // Products already in the cart are skipped. A homepage set left with fewer than `min` moves
        // on to its next products, as after an add to cart (it showed nothing when all its rendered
        // products were in the cart, owner 2026-10-21).
        const inCart = summarize(cart).products;
        const left = this.items.filter((item) => !inCart.has(item.productId)).length;
        if (this.feed && !this.hidden && left < this.min) this.refill().then(() => this.empty && this.showNext());
        else this.plan(cart);
      });
    }
    if (this.hasAttribute('data-set') && !this.hidden) this.prefetchWhenNear();
    if (this.dataset.related) this.loadRelated();
  }

  // Product page: a product outside the theme collections gets Shopify's related products
  // (rendered by sections/ds-bundle-feed.liquid) instead of the generic best sellers from Liquid.
  async loadRelated() {
    this.classList.add('is-loading');
    try {
      const response = await fetch(this.dataset.related, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`DS bundle related ${response.status}`);
      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const kept = this.items.filter((item) => !item.fallback);
      const ids = new Set(kept.map((item) => item.productId));
      const found = [...template.content.querySelectorAll('[data-ds-feed] [data-ds-item]')]
        .filter((el) => !ids.has(Number(el.dataset.productId)))
        .slice(0, this.max - (kept.length - 1));
      // Only swap when the related products alone give at least two suggestions.
      if (found.length + kept.length - 1 >= 2) {
        for (const item of this.items) if (item.fallback) item.el.remove();
        this.listEl.append(...found);
        const added = found.map((el) => new BundleItem(el));
        added.forEach((item) => item.render());
        this.items = [...kept, ...added];
      }
    } catch (error) {
      // Keep the best sellers.
    }
    this.classList.remove('is-loading');
    this.plan(await loadCart());
  }

  disconnectedCallback() {
    bundles.delete(this);
  }

  itemFor(node) {
    const el = node.closest('[data-ds-item]');
    return el ? this.items.find((item) => item.el === el) : null;
  }

  // Show the fewest items (at least `min`) that reach the next reward the cart does not have yet.
  // Products already in the cart are skipped.
  plan(cart) {
    const state = summarize(cart);
    this.state = state;
    const pool = this.items.filter((item) => !state.products.has(item.productId));
    const current = rewardLevel(state.total, state.original, state.shirts);
    const least = Math.min(this.min, pool.length);
    let count = least;
    if (current < tiers().length) {
      count = pool.length;
      for (let n = least; n <= pool.length; n += 1) {
        const sum = pool.slice(0, n).reduce((total, item) => total + item.price, 0);
        if (rewardLevel(state.total + sum, state.original + sum, state.shirts + n) > current) {
          count = n;
          break;
        }
      }
    }
    const shown = new Set(pool.slice(0, count));
    for (const item of this.items) {
      item.el.hidden = !shown.has(item);
      item.checked = true;
      item.render();
    }
    this.poolSize = pool.length;
    this.update();
    this.markSeen();
  }

  update() {
    const chosen = this.items.filter((item) => !item.el.hidden && item.checked && item.current);
    const sum = chosen.reduce((total, item) => total + item.current.price, 0);
    const was = chosen.reduce((total, item) => total + Math.max(item.current.compare || 0, item.current.price), 0);
    this.totalEl.textContent = formatMoney(sum);
    this.compareEl.hidden = !(was > sum);
    this.compareEl.textContent = formatMoney(was);

    const state = this.state || summarize(null);
    const before = rewardLevel(state.total, state.original, state.shirts);
    const total = state.total + sum;
    const original = state.original + sum;
    const shirts = state.shirts + chosen.length;
    const after = rewardLevel(total, original, shirts);
    const next = tiers()[after];
    let text = '';
    if (chosen.length && after > before) {
      text = TEXT.unlock.replace('{reward}', tiers()[after - 1].reward);
    } else if (next && next.kind === 'shirts') {
      // The last shirt of the tier is the free one, so count the paid shirts still needed.
      const left = next.min - 1 - shirts;
      text = left > 0
        ? TEXT.toShirt.replace('{count}', left).replace('{shirts}', left === 1 ? 'shirt' : 'shirts').replace('{reward}', next.more)
        : next.last || '';
    } else if (next) {
      const reached = next.kind === 'total' ? total : original;
      text = TEXT.toSpend.replace('{money}', formatMoney(next.min - reached)).replace('{reward}', next.more);
    }
    this.rewardEl.textContent = text;
    this.rewardEl.hidden = !text;
    this.rewardEl.classList.toggle('is-unlocked', Boolean(chosen.length && after > before));

    if (!this.busy) {
      this.addButton.disabled = !chosen.length;
      this.addLabel.textContent = !chosen.length
        ? TEXT.pick
        : chosen.length === 1
          ? TEXT.addOne
          : TEXT.addMany.replace('{count}', chosen.length);
    }
  }

  // Same add-to-cart flow as assets/ds-cart-upsell.js, so the cart drawer and ladder refresh.
  async add() {
    const chosen = this.items.filter((item) => !item.el.hidden && item.checked && item.current);
    if (!chosen.length || this.busy) return;
    this.busy = true;
    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');
    this.addLabel.textContent = TEXT.adding;
    this.statusEl.textContent = '';
    try {
      const sections = [];
      document.dispatchEvent(new CustomEvent('cart:push:section.id', { detail: { sections } }));
      const sectionIds = [...new Set(sections)].slice(0, 5);
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: chosen.map((item) => ({ id: item.current.id, quantity: 1 })),
          sections: sectionIds,
          sections_url: location.pathname
        })
      });
      const result = await response.json();
      if (!response.ok) {
        this.statusEl.textContent = result.description || TEXT.error;
        return;
      }
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('cart');
      const cart = await cartResponse.json();
      let rendered = result.sections;
      if (sectionIds.length && (!rendered || sectionIds.some((id) => !rendered[id]))) {
        const url = new URL(location.href);
        url.searchParams.set('sections', sectionIds.join(','));
        const sectionsResponse = await fetch(url);
        if (!sectionsResponse.ok) throw new Error('sections');
        rendered = await sectionsResponse.json();
      }
      cart.sections = rendered;
      this.busy = false;
      this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: result.items || [result], cart } }));
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    } catch (error) {
      this.statusEl.textContent = TEXT.error;
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      this.busy = false;
      this.addButton.removeAttribute('aria-busy');
      this.update();
    }
  }

  sets() {
    return this.hasAttribute('data-set') && this.parentElement
      ? [...this.parentElement.querySelectorAll(':scope > ds-bundle[data-set]')]
      : [this];
  }

  // The set after this one, skipping sets with nothing left to show.
  nextSet() {
    const sets = this.sets().filter((set) => set === this || !set.empty);
    return sets.length < 2 ? null : sets[(sets.indexOf(this) + 1) % sets.length];
  }

  show() {
    for (const set of this.sets()) set.hidden = set !== this;
    this.fresh = false;
    this.markSeen();
  }

  async showNext() {
    if (this.switching) return;
    const button = this.querySelector('[data-ds-switch]');
    this.switching = true;
    button?.setAttribute('aria-busy', 'true');
    let next = this.nextSet();
    try {
      for (let tries = this.sets().length; next && next !== this && tries > 0; tries -= 1) {
        // A window prepared earlier may have lost products to the cart since.
        if (next.fresh && next.poolSize < next.min) next.fresh = false;
        await next.refill();
        if (!next.empty && next.items.length) break;
        next = next.nextSet();
      }
    } finally {
      this.switching = false;
      button?.removeAttribute('aria-busy');
    }
    if (!next || next === this || next.empty || !next.items.length) return;
    next.show();
    next.querySelector('[data-ds-switch]')?.focus({ preventScroll: true });
    next.nextSet()?.refill();
  }

  // Homepage sets: remember the products this visit has shown.
  markSeen() {
    if (!this.hasAttribute('data-set') || this.hidden) return;
    for (const item of this.items) {
      if (item.el.hidden) continue;
      seen.add(item.productId);
      this.mine.add(item.productId);
    }
  }

  // Get the next set ready once the shopper scrolls near the sets.
  prefetchWhenNear() {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.nextSet()?.refill();
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(this);
  }

  // Homepage sets: prepare the next products of this set. Does nothing while a prepared window is
  // still waiting to be shown. Runs one at a time per set.
  refill() {
    this.task = this.task
      .then(() => this.fillWindow())
      .catch(() => {
        this.failed = true;
      });
    return this.task;
  }

  // Up to `max` products, best sellers first, skipping products in the cart or already shown in
  // another set this visit. When the whole collection has been shown, the set starts over.
  async fillWindow() {
    if (!this.feed || this.fresh) return;
    const cart = await loadCart();
    const inCart = summarize(cart).products;
    const last = new Set(this.items.map((item) => item.productId));
    // Products of the current window that never made it on screen go back to the front of the queue.
    this.queue.unshift(...this.items.filter((item) => !this.mine.has(item.productId)).map((item) => item.el));
    const picked = [];
    const take = (skip) => {
      while (this.queue.length && picked.length < this.max) {
        const el = this.queue.shift();
        const id = Number(el.dataset.productId);
        if (!inCart.has(id) && !seen.has(id) && !skip.has(id)) picked.push(el);
      }
    };
    let restarted = false;
    for (;;) {
      take(restarted ? last : new Set());
      if (picked.length >= this.max) break;
      if (this.page < this.pages) {
        await this.fetchPage();
        continue;
      }
      if (restarted || !this.mine.size) break;
      // Everything was shown: start over, still skipping what is on screen in other sets.
      restarted = true;
      for (const id of this.mine) seen.delete(id);
      this.mine.clear();
      this.known = new Set(picked.map((el) => Number(el.dataset.productId)));
      this.queue = [];
      this.page = 0;
      this.pages = Infinity;
    }
    if (!picked.length) {
      this.empty = true;
      return;
    }
    this.listEl.replaceChildren(...picked);
    this.items = picked.map((el) => new BundleItem(el));
    this.items.forEach((item) => item.render());
    this.fresh = this.hidden;
    this.plan(cart);
  }

  async fetchPage() {
    const url = new URL(this.feed, location.origin);
    url.searchParams.set('page', String(this.page + 1));
    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`DS bundle feed ${response.status}`);
    const template = document.createElement('template');
    template.innerHTML = await response.text();
    const feed = template.content.querySelector('[data-ds-feed]');
    this.page += 1;
    this.pages = Number(feed?.dataset.pages) || this.page;
    for (const el of feed ? feed.querySelectorAll('[data-ds-item]') : []) {
      const id = Number(el.dataset.productId);
      if (this.known.has(id)) continue;
      this.known.add(id);
      this.queue.push(el);
    }
  }
}

document.addEventListener('cart:change', (event) => {
  const cart = event.detail?.cart;
  if (!cart?.items) return;
  cartRequest = Promise.resolve(cart);
  const inCart = summarize(cart).products;
  bundles.forEach((bundle) => {
    // A homepage set whose products went into the cart moves on to its next products
    // (fillWindow plans it); its unused spare products come first.
    const left = bundle.items.filter((item) => !inCart.has(item.productId)).length;
    if (bundle.feed && !bundle.hidden && left < bundle.min) bundle.refill();
    else bundle.plan(cart);
  });
});

document.addEventListener('cart:refresh', () => {
  loadCart(true).then((cart) => bundles.forEach((bundle) => bundle.plan(cart)));
});

// Theme editor: selecting a set block shows that set.
document.addEventListener('shopify:block:select', (event) => {
  const set = event.target.closest?.('ds-bundle[data-set]');
  if (set) (set.items.length ? Promise.resolve() : set.refill()).then(() => set.show());
});

if (!customElements.get('ds-bundle')) customElements.define('ds-bundle', DsBundle);
